import type { PersonOption } from "@bundle/core";
import { useState } from "react";
import { Avatar, Icon, Pressable, ScrollView, Stack, Text, TextField } from "../../primitives/index.ts";
import { useT } from "../../i18n/I18nProvider.tsx";

export type PeoplePickerProps = {
  people: PersonOption[];
  selected: string[];
  onChange: (userIds: string[]) => void;
  onDone: () => void;
  testID: string;
};

/** Search and toggle workspace members (people and agents) for a person property. */
export function PeoplePicker({ people, selected, onChange, onDone, testID }: PeoplePickerProps) {
  const t = useT();
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const visible = people.filter((p) => q === "" || p.name.toLowerCase().includes(q));
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <Stack gap="1" padding="2" testID={testID}>
      <TextField
        value={query}
        onChange={setQuery}
        placeholder={t("db.person.search")}
        autoFocus
        onSubmit={() => {
          const first = visible[0];
          if (first !== undefined) toggle(first.userId);
        }}
        onCancel={onDone}
        testID={`${testID}-search`}
      />
      <ScrollView>
        <Stack gap="0.5" paddingY="1">
          {visible.map((p) => (
            <Pressable key={p.userId} direction="horizontal" radius="sm" onPress={() => toggle(p.userId)} selected={selected.includes(p.userId)} testID={`${testID}-person-${p.userId}`}>
              <Stack grow direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1">
                <Avatar name={p.name} src={p.avatarUrl} size="avatar.xs" square={p.isAgent} />
                <Stack grow>
                  <Text variant="footnote" lines={1}>
                    {p.name}
                  </Text>
                </Stack>
                {selected.includes(p.userId) ? <Icon name="check" size="icon.sm" color="accent.text" /> : null}
              </Stack>
            </Pressable>
          ))}
        </Stack>
      </ScrollView>
    </Stack>
  );
}
